// ─── Vigilance fournisseurs : espace transporteur mutualisé ──────────────────
//
// Un espace documentaire par TRANSPORTEUR (et non par tenant), identifié par son
// numéro de TVA intracommunautaire. Plusieurs chargeurs se rattachent au même
// espace : le transporteur ne fournit ses pièces qu'une fois.
//
// Ces types sont partagés front/back : le référentiel d'exigences (`VIGILANCE_
// REQUIREMENTS`) est la traduction directe du Code du travail (obligation de
// vigilance) et du Code des transports — c'est la loi, pas un paramètre client,
// d'où la constante plutôt qu'une table.
//
// À ne pas confondre avec `ICarrierDocument` (coffre `carrier_documents`), qui
// reste le coffre libre et PRIVÉ d'un tenant sur une de ses fiches.

/** Pièces attendues au titre de la vigilance. `AUTRE` est hors note. */
export type VigilanceDocumentType =
  | 'URSSAF_VIGILANCE'
  | 'IMMATRICULATION'
  | 'LICENCE_TRANSPORT'
  | 'ASSURANCE_RC'
  | 'ASSURANCE_MARCHANDISES'
  | 'SALARIES_ETRANGERS'
  | 'REGULARITE_FISCALE'
  | 'CAPACITE_PROFESSIONNELLE'
  | 'RIB'
  | 'CONTRAT'
  | 'AUTRE';

/** Une exigence du référentiel légal. */
export interface IVigilanceRequirement {
  type: VigilanceDocumentType;
  /** Libellé FR de la pièce. */
  label: string;
  /**
   * Une pièce obligatoire manquante/expirée force la bande ROUGE, quelle que
   * soit la note (gate dur).
   */
  mandatory: boolean;
  /**
   * Périodicité de renouvellement en mois. Sert à DÉRIVER l'expiration quand le
   * dépôt ne porte pas de `validTo` : une attestation de vigilance émise il y a
   * sept mois est expirée même sans date de fin saisie. `null` = permanent.
   */
  renewalMonths: number | null;
  /** Poids dans la note (les pièces obligatoires pèsent plus). */
  weight: number;
  /** Fondement juridique, affiché tel quel dans l'UI. */
  legalBasis: string;
  /** Ce que la pièce prouve, en une phrase (aide au dépôt côté transporteur). */
  description: string;
}

/**
 * Référentiel de la vigilance fournisseur transport. Ordre = ordre d'affichage
 * (obligatoires d'abord, du plus contraignant au moins).
 */
export const VIGILANCE_REQUIREMENTS: IVigilanceRequirement[] = [
  {
    type: 'URSSAF_VIGILANCE',
    label: 'Attestation de vigilance URSSAF',
    mandatory: true,
    renewalMonths: 6,
    weight: 3,
    legalBasis: 'C. trav. art. L.8222-1 et D.8222-5',
    description:
      "Prouve que le transporteur est à jour de ses déclarations et du paiement de ses cotisations sociales. À renouveler tous les six mois jusqu'à la fin du contrat.",
  },
  {
    type: 'IMMATRICULATION',
    label: "Justificatif d'immatriculation (Kbis, avis SIRENE)",
    mandatory: true,
    renewalMonths: 3,
    weight: 2,
    legalBasis: 'C. trav. art. D.8222-5, 1°',
    description:
      "Extrait Kbis de moins de trois mois, avis de situation au répertoire SIRENE ou carte d'artisan : atteste de l'existence légale de l'entreprise.",
  },
  {
    type: 'LICENCE_TRANSPORT',
    label: 'Licence de transport (communautaire ou intérieure)',
    mandatory: true,
    renewalMonths: null,
    weight: 3,
    legalBasis: 'C. transports art. R.3211-1 et suivants',
    description:
      "Titre d'exploitation délivré par la DREAL, avec ses copies conformes. Sans licence valide, le transporteur ne peut pas exécuter le transport.",
  },
  {
    type: 'ASSURANCE_RC',
    label: 'Attestation de responsabilité civile professionnelle',
    mandatory: true,
    renewalMonths: 12,
    weight: 2,
    legalBasis: 'Contractuel',
    description:
      "Couvre les dommages causés aux tiers dans l'exercice de l'activité. Généralement annuelle.",
  },
  {
    type: 'ASSURANCE_MARCHANDISES',
    label: 'Attestation d’assurance marchandises transportées',
    mandatory: true,
    renewalMonths: 12,
    weight: 2,
    legalBasis: 'Contractuel / Convention CMR',
    description:
      'Couvre la marchandise confiée pendant le transport, au-delà des limites légales de responsabilité du transporteur.',
  },
  {
    type: 'SALARIES_ETRANGERS',
    label: 'Liste nominative des salariés étrangers',
    mandatory: true,
    renewalMonths: 6,
    weight: 1,
    legalBasis: 'C. trav. art. D.8254-2',
    description:
      "Liste des salariés étrangers soumis à autorisation de travail, ou attestation sur l'honneur de leur absence. À renouveler tous les six mois.",
  },
  {
    type: 'REGULARITE_FISCALE',
    label: 'Attestation de régularité fiscale',
    mandatory: false,
    renewalMonths: 12,
    weight: 1,
    legalBasis: 'Bonne pratique (marchés publics : CCP art. R.2143-7)',
    description:
      "Atteste que le transporteur est à jour de ses obligations déclaratives et de paiement en matière fiscale.",
  },
  {
    type: 'CAPACITE_PROFESSIONNELLE',
    label: 'Attestation de capacité professionnelle',
    mandatory: false,
    renewalMonths: null,
    weight: 1,
    legalBasis: 'C. transports art. R.3211-33',
    description:
      "Justifie la compétence du gestionnaire de transport, condition d'accès à la profession.",
  },
  {
    type: 'RIB',
    label: 'RIB / coordonnées bancaires',
    mandatory: false,
    renewalMonths: null,
    weight: 1,
    legalBasis: 'Opérationnel',
    description: 'Coordonnées bancaires utilisées pour le règlement des factures.',
  },
  {
    type: 'CONTRAT',
    label: 'Contrat de sous-traitance signé',
    mandatory: false,
    renewalMonths: null,
    weight: 1,
    legalBasis: 'Contractuel',
    description: 'Contrat cadre ou contrat de sous-traitance signé entre les deux parties.',
  },
  {
    type: 'AUTRE',
    label: 'Autre document',
    mandatory: false,
    renewalMonths: null,
    weight: 0,
    legalBasis: '—',
    description: "Toute autre pièce utile, hors référentiel légal. N'entre pas dans la note.",
  },
];

/** Accès indexé au référentiel, pour l'UI comme pour le moteur de note. */
export const VIGILANCE_REQUIREMENT_BY_TYPE: Record<VigilanceDocumentType, IVigilanceRequirement> =
  VIGILANCE_REQUIREMENTS.reduce(
    (acc, req) => {
      acc[req.type] = req;
      return acc;
    },
    {} as Record<VigilanceDocumentType, IVigilanceRequirement>,
  );

/** Cycle de vie d'un espace. */
export type VigilanceSpaceStatus =
  /** Créé par un chargeur, le transporteur n'a pas encore été sollicité. */
  | 'DRAFT'
  /** Lien du portail envoyé au transporteur, en attente de dépôt. */
  | 'INVITED'
  /** Au moins une pièce déposée. */
  | 'ACTIVE'
  /** Espace mis de côté (transporteur radié, doublon). */
  | 'ARCHIVED';

/** Résultat de la revue d'une pièce par le superadmin. */
export type VigilanceReviewStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

/** Qui a déposé la pièce. */
export type VigilanceUploadSource = 'CARRIER' | 'SHIPPER' | 'SUPERADMIN';

/** État d'une exigence, du meilleur au pire. */
export type VigilanceRequirementState =
  | 'VALID'
  | 'EXPIRING_SOON'
  | 'PENDING_REVIEW'
  | 'REJECTED'
  | 'EXPIRED'
  | 'MISSING';

/** Facteur appliqué au poids de l'exigence selon son état. */
export const VIGILANCE_STATE_FACTOR: Record<VigilanceRequirementState, number> = {
  VALID: 1,
  EXPIRING_SOON: 0.8,
  PENDING_REVIEW: 0.5,
  REJECTED: 0,
  EXPIRED: 0,
  MISSING: 0,
};

/** Seuil (en jours) sous lequel une pièce est signalée comme expirant bientôt. */
export const VIGILANCE_EXPIRING_SOON_DAYS = 30;

export type VigilanceGrade = 'A' | 'B' | 'C' | 'D' | 'E';
export type VigilanceBand = 'GREEN' | 'ORANGE' | 'RED';

/** Métadonnées d'une pièce déposée (le binaire reste côté serveur). */
export interface IVigilanceDocument {
  id: string;
  spaceId: string;
  type: VigilanceDocumentType;
  fileName: string;
  mimeType: string | null;
  fileSize: number;
  /** Date d'émission — sert à dériver l'expiration si `validTo` est absent. */
  issuedAt: string | null;
  validFrom: string | null;
  validTo: string | null;
  issuedBy: string | null;
  /** Référence du document chez l'émetteur (n° d'attestation, n° de police…). */
  reference: string | null;
  reviewStatus: VigilanceReviewStatus;
  reviewedAt: string | null;
  rejectionReason: string | null;
  uploadSource: VigilanceUploadSource;
  /** Qui a déposé, en clair (e-mail ou nom) — traçabilité. */
  uploadedByLabel: string | null;
  /** Une pièce remplacée par un dépôt plus récent sort de la note. */
  superseded: boolean;
  supersedesId: string | null;
  note: string | null;
  createdAt: string;
  updatedAt: string;
}

// ─── Constat des référentiels publics ───────────────────────────────────────
//
// La qualification transporteur interroge déjà VIES (validité de la TVA), le
// registre national des transporteurs (licence + copies conformes) et le
// registre des entreprises (activité). Ce que ces sources AFFIRMENT n'a pas à
// être redemandé au transporteur sous forme de PDF : le constat est archivé sur
// l'espace et vaut preuve pour les exigences qu'il couvre.

/** Origine de la preuve retenue pour une exigence. */
export type VigilanceEvidenceOrigin =
  /** Une pièce déposée dans l'espace. */
  | 'DOCUMENT'
  /** Un référentiel public consulté (registre transporteurs, registre national). */
  | 'REGISTRY';

/** Une licence de transport constatée au registre national. */
export interface IVigilanceRegistryLicence {
  /** `LC` : licence communautaire (> 3,5 t / international). `LTI` : intérieure. */
  kind: 'LC' | 'LTI';
  number: string | null;
  /** Fin de validité au registre (ISO). `null` = inconnue. */
  validUntil: string | null;
  /** Copies conformes ≈ nombre de véhicules autorisés. */
  copies: number | null;
}

/**
 * Ce que les référentiels publics disaient du transporteur lors de la dernière
 * qualification. Archivé sur l'espace (`enrichment`) pour que la conformité
 * documentaire et la fiabilité racontent la même histoire, sans re-solliciter
 * les sources à chaque lecture.
 */
export interface IVigilanceRegistrySnapshot {
  /** Horodatage de la qualification qui a produit ce constat (ISO). */
  checkedAt: string;
  /** TVA valide au VIES. `null` = non vérifiée (pas de source pour ce pays). */
  vatValid: boolean | null;
  /** Entreprise active au registre national. `null` = non vérifiée. */
  registryActive: boolean | null;
  /** Sanctions / PEP. `UNKNOWN` = non vérifié, ce n'est PAS un « clean ». */
  sanctionStatus: 'CLEAR' | 'LISTED' | 'UNKNOWN';
  /** Licences transport constatées. Vide = aucune au registre. */
  licences: IVigilanceRegistryLicence[];
  /** Inscrit au registre des commissionnaires. `null` = non vérifié. */
  commissionnaire: boolean | null;
  /** Identité telle que la voient les registres. */
  legalName: string | null;
  siren: string | null;
  country: string | null;
  /** Note de fiabilité au moment du constat — indicative, pas la note documentaire. */
  riskScore: number | null;
  riskGrade: string | null;
  riskBand: 'green' | 'orange' | 'red' | null;
  /** Motifs de blocage dur relevés par la qualification. */
  riskGatedReason: string[];
  /** Traçabilité : quelle source, interrogée quand, avec quel succès. */
  sources: Array<{ name: string; ok: boolean; fetchedAt: string }>;
}

/** État calculé d'une exigence pour un espace donné. */
export interface IVigilanceRequirementStatus {
  type: VigilanceDocumentType;
  label: string;
  mandatory: boolean;
  legalBasis: string;
  state: VigilanceRequirementState;
  /** Pièce courante retenue pour cet état (la plus récente non remplacée). */
  documentId: string | null;
  /** Expiration effective : `validTo` saisi, ou dérivée de l'émission. */
  expiresAt: string | null;
  /** Négatif si déjà expiré. `null` si la pièce est permanente ou absente. */
  daysUntilExpiry: number | null;
  /** Vrai quand l'expiration est dérivée de la périodicité, pas saisie. */
  expiryDerived: boolean;
  /**
   * D'où vient l'état ci-dessus : une pièce déposée, ou un registre public qui
   * atteste déjà du fait. `null` quand rien ne couvre l'exigence.
   */
  evidence: VigilanceEvidenceOrigin | null;
  /**
   * Libellé de la source publique retenue (« Registre national des
   * transporteurs »…). `null` dès que la preuve est une pièce.
   */
  evidenceSource: string | null;
}

/** Note de conformité documentaire d'un espace. */
export interface IVigilanceScore {
  /** 0..100, arrondi à l'entier. */
  score: number;
  grade: VigilanceGrade;
  band: VigilanceBand;
  /** État détaillé de chaque exigence notée. */
  requirements: IVigilanceRequirementStatus[];
  /** Motifs de la bande ROUGE (pièces obligatoires en défaut). */
  blockingReasons: string[];
  /** Compteurs pour les tableaux de bord. */
  counts: {
    valid: number;
    expiringSoon: number;
    pendingReview: number;
    expired: number;
    missing: number;
    rejected: number;
  };
  computedAt: string;
}

/** L'espace documentaire d'un transporteur (vue chargeur ou superadmin). */
export interface IVigilanceSpace {
  id: string;
  /** Identifiant métier : TVA intracommunautaire normalisée. */
  vatNumber: string;
  vatCountry: string | null;
  legalName: string | null;
  siren: string | null;
  siret: string | null;
  contactEmail: string | null;
  contactName: string | null;
  status: VigilanceSpaceStatus;
  score: number | null;
  grade: VigilanceGrade | null;
  band: VigilanceBand | null;
  computedAt: string | null;
  invitedAt: string | null;
  lastUploadAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Ligne du tableau de bord conformité côté chargeur. */
export interface IVigilanceSpaceSummary extends IVigilanceSpace {
  /** Fiche transporteur locale rattachée, si le rattachement en porte une. */
  carrierId: string | null;
  carrierName: string | null;
  /** Nombre de pièces courantes déposées. */
  documentCount: number;
  /** Pièces obligatoires en défaut (manquantes, expirées, refusées). */
  missingMandatory: VigilanceDocumentType[];
  /** Pièces expirant sous 30 jours. */
  expiringSoon: VigilanceDocumentType[];
}

/** Détail d'un espace : identité, pièces et note. */
export interface IVigilanceSpaceDetail {
  space: IVigilanceSpace;
  carrierId: string | null;
  documents: IVigilanceDocument[];
  /** Pièces remplacées par un dépôt plus récent — preuve de la vigilance passée. */
  history: IVigilanceDocument[];
  score: IVigilanceScore;
  /**
   * Dernier constat des référentiels publics, `null` tant que le transporteur
   * n'a pas été qualifié. C'est ce qui relie la conformité documentaire à la
   * fiabilité : les deux vues lisent le même constat.
   */
  registry: IVigilanceRegistrySnapshot | null;
  /**
   * Le dossier de l'attestation de vigilance : ce que le PDF a livré, ce que
   * les contrôles en disent, et où en est sa vérification à l'URSSAF. C'est la
   * seule pièce qui a son propre parcours, parce que c'est la seule dont
   * l'authenticité se vérifie à la source.
   */
  urssaf: IUrssafAttestation;
}

/** Réponse de la réclamation d'un espace par numéro de TVA. */
export interface IVigilanceClaimResult {
  /** Vrai si l'espace vient d'être créé (TVA inconnue de la plateforme). */
  created: boolean;
  /** Vrai si le tenant vient d'être rattaché à un espace déjà existant. */
  linked: boolean;
  detail: IVigilanceSpaceDetail;
}

/** Vue du portail transporteur (tokenisée). Aucune donnée chargeur. */
export interface IVigilancePortalView {
  space: Pick<
    IVigilanceSpace,
    'id' | 'vatNumber' | 'legalName' | 'contactEmail' | 'status' | 'score' | 'grade' | 'band'
  >;
  requirements: IVigilanceRequirement[];
  documents: IVigilanceDocument[];
  score: IVigilanceScore;
  /**
   * Ce que les registres publics attestent déjà. Le transporteur le voit pour
   * ne pas déposer une pièce qu'on n'a pas à lui demander (sa licence, par
   * exemple, quand le registre national la donne).
   */
  registry: IVigilanceRegistrySnapshot | null;
  /**
   * Le dossier de l'attestation de vigilance : ce que le PDF a livré, ce que
   * les contrôles en disent, et où en est sa vérification à l'URSSAF. C'est la
   * seule pièce qui a son propre parcours, parce que c'est la seule dont
   * l'authenticité se vérifie à la source.
   */
  urssaf: IUrssafAttestation;
}

/** Vue superadmin d'un espace : ajoute les chargeurs rattachés. */
export interface IVigilanceSpaceAdminDetail extends IVigilanceSpaceDetail {
  links: Array<{
    companyId: string;
    companyName: string | null;
    carrierId: string | null;
    createdAt: string;
  }>;
  portalUrl: string | null;
  tokenExpiresAt: string | null;
}

// ─── Attestation de vigilance URSSAF : extraction, contrôles, vérification ───
//
// L'attestation de vigilance est la seule pièce du référentiel dont
// l'authenticité se vérifie à la source : l'URSSAF publie une page de contrôle
// où l'on saisit le code de sécurité imprimé sur le PDF. Cette page est
// protégée par un captcha — **la vérification ne peut pas être automatisée**,
// et il ne faut pas essayer. Le produit fait donc tout le reste : il lit le
// PDF, contrôle ce qui est contrôlable sans sortir, et ne demande à l'humain
// que le geste qu'aucune machine ne peut faire.

/** Durée de validité légale d'une attestation, à compter de sa délivrance. */
export const URSSAF_VALIDITY_MONTHS = 6;

/** À partir de quand on relance : il reste un mois pour obtenir la suivante. */
export const URSSAF_RENEW_MONTHS = 5;

/** Longueur du code de sécurité imprimé sur l'attestation. */
export const URSSAF_SECURITY_CODE_LENGTH = 15;

/** La page de vérification de l'URSSAF. Non pré-remplissable par l'URL. */
export const URSSAF_VERIFICATION_URL =
  'https://www.urssaf.fr/accueil/outils-documentation/outils/verification-attestation.html';

/**
 * Ce que le produit a lu dans le PDF. Chaque champ est éditable côté interface :
 * une extraction ratée doit rester rattrapable à la main, jamais bloquante.
 */
export interface IUrssafExtraction {
  /** Dénomination sociale portée par l'attestation. */
  legalName: string | null;
  siren: string | null;
  /** Établissements couverts (SIRET à 14 chiffres). */
  sirets: string[];
  /** « Attestation délivrée le … ». C'est elle qui ouvre les six mois. */
  issuedAt: string | null;
  /** « À jour au … » : exigibilité de la dernière période traitée. */
  upToDateAt: string | null;
  /** Effectif déclaré. Absent pour un indépendant sans salarié. */
  headcount: number | null;
  /** Masse salariale déclarée, en euros. */
  payroll: number | null;
  /** Code de sécurité — 15 caractères, la clé de la vérification URSSAF. */
  securityCode: string | null;
  /**
   * Le PDF portait-il une couche texte ? `false` = document scanné : rien n'est
   * contrôlable, et c'est le premier signal d'une pièce refabriquée.
   */
  hasText: boolean;
  /** Champs corrigés à la main après extraction, pour la traçabilité. */
  editedFields: string[];
}

/** Un contrôle automatique, rendu en clair par l'interface. */
export type UrssafCheckCode =
  /** SIREN du PDF = SIREN de la fiche transporteur. */
  | 'SIREN_MATCH'
  | 'SIREN_MISMATCH'
  /** Aucun SIREN sur la fiche : le rapprochement n'a pas pu être fait. */
  | 'SIREN_UNKNOWN'
  | 'WITHIN_VALIDITY'
  | 'EXPIRED'
  /** Pas de date de délivrance : la fenêtre de six mois est incalculable. */
  | 'ISSUED_AT_MISSING'
  /** Sans code, l'attestation n'est pas vérifiable auprès de l'URSSAF. */
  | 'SECURITY_CODE_MISSING'
  /** PDF sans couche texte : redemander l'original téléchargé sur urssaf.fr. */
  | 'SCANNED_PDF'
  /** Le même code de sécurité sert déjà à un autre transporteur. */
  | 'SECURITY_CODE_REUSED';

/**
 * `BLOCK` interdit de passer à la vérification : la pièce est à refaire ou à
 * redemander. `WARN` laisse continuer en connaissance de cause.
 */
export type UrssafCheckLevel = 'OK' | 'WARN' | 'BLOCK';

export interface IUrssafCheck {
  code: UrssafCheckCode;
  level: UrssafCheckLevel;
  /** Valeurs à injecter dans le libellé (SIREN lus, nom du transporteur…). */
  params?: Record<string, string | number>;
}

/**
 * Lecture d'un PDF avant dépôt : ce qui a été lu ET ce que les contrôles en
 * disent. Les deux ensemble, parce qu'afficher un SIREN sans dire s'il est
 * celui du transporteur ne vérifie rien.
 */
export interface IUrssafAnalysis {
  extraction: IUrssafExtraction;
  checks: IUrssafCheck[];
}

/** Verdict rendu par l'humain après consultation de la page URSSAF. */
export type UrssafVerificationOutcome = 'AUTHENTIC' | 'INVALID';

/** Traçabilité de la vérification : c'est elle qui vaut preuve en contrôle. */
export interface IUrssafVerification {
  outcome: UrssafVerificationOutcome;
  verifiedAt: string;
  /** Qui a vérifié, en clair. */
  verifiedByLabel: string | null;
  /** Une capture du résultat URSSAF a-t-elle été jointe ? */
  hasProof: boolean;
}

/**
 * Statut d'une attestation, du pire au meilleur usage. Il se **calcule** à la
 * lecture, à partir de la date de délivrance et de la vérification : aucune
 * tâche de fond n'a à le faire vieillir, il vieillit tout seul.
 */
export type UrssafStatus =
  /** Rien d'importé. */
  | 'NONE'
  /** Un contrôle bloquant est tombé : pièce à corriger ou à redemander. */
  | 'TO_FIX'
  /** Cohérente, mais l'authenticité n'a pas encore été vérifiée à l'URSSAF. */
  | 'TO_VERIFY'
  /** Vérifiée authentique et dans ses cinq premiers mois. */
  | 'VALID'
  /** Encore valable, mais il est temps de demander la suivante. */
  | 'TO_RENEW'
  /** Plus de six mois : elle ne couvre plus rien. */
  | 'LAPSED'
  /** L'URSSAF a répondu que l'attestation n'est pas authentique. */
  | 'REFUSED';

/** Les statuts qui n'exonèrent pas le donneur d'ordre de sa solidarité. */
export const URSSAF_NON_COMPLIANT: UrssafStatus[] = ['NONE', 'TO_FIX', 'LAPSED', 'REFUSED'];

/** Le dossier URSSAF d'un transporteur, tel que le rend l'API. */
export interface IUrssafAttestation {
  /** Pièce d'origine, `null` quand rien n'a été déposé. */
  documentId: string | null;
  status: UrssafStatus;
  extraction: IUrssafExtraction | null;
  checks: IUrssafCheck[];
  /** Délivrance + six mois. Calculé, jamais lu dans le PDF. */
  validUntil: string | null;
  /** Jours restants avant échéance. Négatif = échue depuis. */
  daysUntilExpiry: number | null;
  verification: IUrssafVerification | null;
}
